
@if (detail(); as item) {
  <div class="page-header lesson-page-header">
    <div>
      <span class="kicker">Modul {{ item.enrollment.moduleNumber }} · {{ item.enrollment.courseTitle }}</span>
      <h1 class="mb-4">{{ item.lesson.title }}</h1>
    </div>
    <a class="ghost-button as-link" routerLink="/learning">Zurück zum Lernbereich</a>
  </div>

  <section class="lesson-page-layout">
    <section class="panel lesson-mobile-overview">
      <div class="lesson-mobile-progress-head">
        <div>
          <span class="kicker">Modul {{ item.enrollment.moduleNumber }}</span>
          <h2>Dein Fortschritt</h2>
        </div>
        <strong>{{ item.enrollment.completedLessons }} / {{ item.enrollment.totalLessons }}</strong>
      </div>
      <div class="progress-track">
        <div class="progress-fill" [style.width.%]="item.enrollment.progressPercent"></div>
      </div>

      <details class="lesson-mobile-picker">
        <summary>
          <span>
            <small>Aktuelle Lektion</small>
            <strong>{{ item.lesson.lessonOrder }}. {{ item.lesson.title }}</strong>
          </span>
          <span class="lesson-picker-toggle">Alle Lektionen</span>
        </summary>
        <nav class="lesson-picker-list" aria-label="Lektionen dieses Moduls">
          @for (lesson of item.enrollment.lessons; track lesson.id) {
            <a
              class="lesson-picker-link"
              [class.active]="lesson.id === item.lesson.id"
              [class.opacity-60]="!lessonAccessible(item, lesson)"
              [class.pointer-events-none]="!lessonAccessible(item, lesson)"
              [attr.aria-disabled]="!lessonAccessible(item, lesson)"
              [routerLink]="lessonAccessible(item, lesson) ? ['/learning', item.enrollment.id, 'lessons', lesson.id] : null"
            >
              <span class="lesson-picker-number">{{ lesson.lessonOrder }}</span>
              <span>
                <strong>{{ lesson.title }}</strong>
                <small>{{ lesson.completed ? 'Erledigt' : lesson.id === item.lesson.id ? 'Aktuell' : lessonAccessible(item, lesson) ? 'Offen' : 'Gesperrt' }}</small>
              </span>
            </a>
          }
        </nav>
      </details>
    </section>

    <div class="lesson-main-content">
      <section class="panel">
        <div class="progress-heading">
          <strong>Lektionsfortschritt</strong>
          <span>{{ item.lesson.completed ? 'Abgeschlossen' : 'In Bearbeitung' }}</span>
        </div>
        <div class="lesson-progress-cards">
          <article class="rounded-lg border border-app-border bg-app-elevated p-3">
            <span class="kicker">Video/Inhalt</span>
            <strong class="mt-1 block">{{ item.lesson.videoWatched ? 'erledigt' : 'offen' }}</strong>
          </article>
          <article class="rounded-lg border border-app-border bg-app-elevated p-3">
            <span class="kicker">Zusammenfassung</span>
            <strong class="mt-1 block">{{ item.lesson.summaryRead ? 'gelesen' : 'offen' }}</strong>
          </article>
          <article class="rounded-lg border border-app-border bg-app-elevated p-3">
            <span class="kicker">Wissens-Check</span>
            <strong class="mt-1 block">{{ item.lesson.completed ? 'bestanden' : 'offen' }}</strong>
          </article>
        </div>
      </section>

      <section class="panel">
        <h2 class="mt-0 text-2xl font-bold">Lektionsinhalt</h2>

        @if (selectedVideoUrl(item)) {
          <div class="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <span class="kicker">Schulungsvideo</span>
              <h3 class="mt-1 text-xl font-bold">{{ selectedVideoLabel(item) }}</h3>
            </div>

            @if (item.lesson.availableVideos.length > 1) {
              <label class="grid min-w-[220px] gap-2">
                <span class="text-xs font-bold uppercase tracking-wide text-app-muted">Video-Sprache</span>
                <select
                  class="rounded-lg border border-app-border bg-app-surface px-3 py-2 font-semibold text-app-ink"
                  [value]="selectedVideoLanguage() || selectedVideo(item)?.language || ''"
                  (change)="changeVideoLanguage($any($event.target).value)"
                >
                  @for (video of item.lesson.availableVideos; track video.id) {
                    <option [value]="video.language">
                      {{ videoLanguageLabel(video.language) }}{{ video.title ? ' · ' + video.title : '' }}
                    </option>
                  }
                </select>
              </label>
            }
          </div>

          @if (selectedVideoIsEmbedded(item)) {
            <div class="rounded-lg border border-app-border bg-black">
              <iframe
                class="h-[56vw] max-h-[680px] w-full rounded-lg"
                [src]="selectedVideoIframeUrl(item)"
                allow="encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                referrerpolicy="strict-origin-when-cross-origin"
              ></iframe>
            </div>
          } @else {
            <video
              class="w-full rounded-lg border border-app-border bg-black"
              controls
              controlsList="nodownload"
              disablePictureInPicture
              [src]="selectedVideoUrl(item)"
              (contextmenu)="$event.preventDefault()"
            ></video>
          }
        } @else {
          <div class="rounded-lg border border-dashed border-app-border bg-app-elevated p-5">
            <span class="kicker">Demo-Inhalt</span>
            <p class="mb-0 text-app-muted">
              Für diese Demo-Lektion ist noch keine Videodatei hinterlegt. Der fachliche Inhalt steht als Text bereit.
            </p>
          </div>
        }

        @if (!item.lesson.videoWatched) {
          <div class="mt-5 rounded-lg border border-app-border bg-app-elevated p-4">
            <h3 class="mt-0 text-xl font-bold">Video bestätigen</h3>
            <p class="mb-3 text-app-muted">
              Sieh dir das Video vollständig an und bestätige anschließend, dass du es gesehen hast. Danach wird die Zusammenfassung angezeigt.
            </p>
            <button class="primary-button" type="button" (click)="markVideoWatched(item)" [disabled]="saving()">
              Ich habe das Video gesehen
            </button>
          </div>
        } @else {
          <h3 class="mt-5 text-xl font-bold">Zusammenfassung</h3>
          @if (item.lesson.description) {
            <div class="lesson-rich-text text-app-ink" [innerHTML]="item.lesson.description"></div>
          }
          @if (item.lesson.transcript) {
            <div class="lesson-rich-text text-app-ink" [innerHTML]="item.lesson.transcript"></div>
          }

          @if (!item.lesson.summaryRead) {
            <button class="primary-button" type="button" (click)="markSummaryRead(item)" [disabled]="saving()">
              Inhalt als gelesen markieren
            </button>
          } @else {
          <p class="text-sm text-app-muted">Zusammenfassung gelesen. Schließe jetzt den verpflichtenden Wissens-Check ab.</p>
          }
        }
      </section>

      <section class="panel">
        @if (!item.lesson.summaryRead) {
          <p class="mb-0 text-app-muted">Der Wissens-Check wird angezeigt, sobald du die Zusammenfassung als gelesen markiert hast.</p>
        } @else {
          <div class="learning-check-teaser">
            <div>
              <span class="kicker">Verpflichtender Abschluss der Lektion</span>
              <h2 class="mt-1 text-2xl font-bold">Wissens-Check</h2>
              <p class="mb-0 text-app-muted">
                Die Lektion wird erst abgeschlossen, nachdem du den Wissens-Check bestanden hast.
              </p>
            </div>
            @if (item.lesson.questions.length) {
              <div class="flex flex-wrap gap-2">
                <a
                  class="primary-button as-link whitespace-nowrap"
                  [routerLink]="['/learning', item.enrollment.id, 'lessons', item.lesson.id, 'knowledge-check']"
                >
                  {{ item.lesson.completed ? 'Ergebnis ansehen' : 'Wissens-Check starten' }}
                </a>
              </div>
            } @else {
              <button class="primary-button" type="button" (click)="completeLesson(item)" [disabled]="saving() || item.lesson.completed">
                Lektion abschließen
              </button>
            }
          </div>
        }
      </section>
    </div>

    <aside class="lesson-sidebar">
      <section class="panel lesson-sidebar-progress">
        <h2 class="mt-0 text-xl font-bold">Modulfortschritt</h2>
        <div class="progress-track">
          <div class="progress-fill" [style.width.%]="item.enrollment.progressPercent"></div>
        </div>
        <p class="text-app-muted">
          {{ item.enrollment.completedLessons }} von {{ item.enrollment.totalLessons }} Lektionen abgeschlossen.
        </p>
      </section>

      <section class="panel lesson-sidebar-list">
        <h2 class="mt-0 text-xl font-bold">Lektionen</h2>
        <div class="lesson-desktop-list">
          @for (lesson of item.enrollment.lessons; track lesson.id) {
            <a
              class="rounded-lg border border-app-border bg-app-elevated px-3 py-2 text-app-ink no-underline hover:border-app-brand"
              [class.border-app-brand]="lesson.id === item.lesson.id"
              [class.opacity-60]="!lessonAccessible(item, lesson)"
              [class.pointer-events-none]="!lessonAccessible(item, lesson)"
              [attr.aria-disabled]="!lessonAccessible(item, lesson)"
              [routerLink]="lessonAccessible(item, lesson) ? ['/learning', item.enrollment.id, 'lessons', lesson.id] : null"
            >
              <strong>{{ lesson.lessonOrder }}. {{ lesson.title }}</strong>
              <small class="block text-app-muted">{{ lesson.completed ? 'erledigt' : lessonAccessible(item, lesson) ? 'offen' : 'gesperrt' }}</small>
            </a>
          }
        </div>
      </section>

      <section class="panel lesson-sidebar-navigation">
        <h2 class="mt-0 text-xl font-bold">Navigation</h2>
        <div class="grid gap-2">
          @if (item.lesson.previousLessonId) {
            <a class="ghost-button as-link justify-center" [routerLink]="['/learning', item.enrollment.id, 'lessons', item.lesson.previousLessonId]">
              Vorherige Lektion
            </a>
          }
          @if (item.lesson.nextLessonId) {
            @if (item.lesson.completed) {
              <a class="primary-button as-link justify-center" [routerLink]="['/learning', item.enrollment.id, 'lessons', item.lesson.nextLessonId]">
                Nächste Lektion
              </a>
            } @else {
              <button class="primary-button justify-center" type="button" disabled>
                Erst Wissens-Check bestehen
              </button>
            }
          } @else {
            @if (item.lesson.completed && item.enrollment.finalQuiz?.available && !item.enrollment.finalQuiz?.passed) {
              <div class="final-quiz-ready-panel compact">
                <div>
                  <strong>Alle Lektionen bestanden</strong>
                  <p>Die Abschlussprüfung kann jetzt absolviert werden.</p>
                </div>
                <a
                  class="primary-button as-link justify-center"
                  [routerLink]="['/learning', item.enrollment.id, 'final-knowledge-check']"
                >
                  Zur Abschlussprüfung →
                </a>
              </div>
            } @else if (!item.lesson.completed) {
              <button class="primary-button justify-center" type="button" disabled>
                Erst Wissens-Check bestehen
              </button>
            } @else {
              <a class="ghost-button as-link justify-center" routerLink="/learning">Zur Übersicht</a>
            }
          }
        </div>
      </section>
    </aside>
  </section>
} @else {
  <div class="empty">Lektion wird geladen.</div>
}
  

